import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { SocialMediaReport, ReportComparison, TrendDataPoint } from "../hooks/useSocialMediaReport";
import { PLATFORM_REGISTRY } from "@/config/platforms";
import { UsersIcon } from "@/components/icons";
import { IconProps } from "@/components/icons/types";
import {
  ReportHeadline,
  ReportPanel,
  ReportPanelEmpty,
  ReportPanelNote,
  ReportStat,
  ReportStats,
} from "./ReportPanel";
import {
  ChartBox,
  QuietList,
  SOURCE_COLOR,
  SourceBadge,
  Sparkline,
  VizHeading,
  type QuietRow,
} from "./ReportViz";

const TICK = { fontSize: 10, fill: "#9CA3AF" };

const icon = (
  <SourceBadge color={SOURCE_COLOR.social}>
    <UsersIcon className="w-3.5 h-3.5" />
  </SourceBadge>
);

interface FollowerPanelProps {
  report: SocialMediaReport;
  comparison?: ReportComparison | null;
  historicalData: TrendDataPoint[];
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatChange(value: number): string {
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${formatNumber(value)}`;
}

interface PlatformFollowerData {
  key: string;
  label: string;
  color: string;
  Icon: React.ComponentType<IconProps>;
  config: (typeof PLATFORM_REGISTRY)[string];
  count: number;
  change?: { value: number; percentage: number };
  gained?: number;
  lost?: number;
  growth?: number;
}

function extractPlatformFollowers(
  report: SocialMediaReport,
  comparison?: ReportComparison | null,
): PlatformFollowerData[] {
  const platforms: PlatformFollowerData[] = [];

  for (const [key, config] of Object.entries(PLATFORM_REGISTRY)) {
    const platformData = report[key as keyof SocialMediaReport] as Record<string, number> | null;
    if (!platformData) continue;

    const count = platformData[config.followersKey] ?? 0;
    if (count === 0) continue;

    const changeMap: Record<string, string> = { facebook: "fans", instagram: "followers" };
    const changeKey = changeMap[key];
    const platformChanges = comparison?.changes[key as keyof typeof comparison.changes] as
      | Record<string, { value: number; percentage: number }>
      | null
      | undefined;

    const entry: PlatformFollowerData = {
      key,
      label: config.label,
      color: config.color,
      Icon: config.iconComponent,
      config,
      count,
      change: changeKey ? platformChanges?.[changeKey] : undefined,
    };

    if (key === "facebook") {
      entry.gained = platformData.totalFanAdds ?? 0;
      entry.lost = platformData.totalFanRemoves ?? 0;
    }
    if (key === "instagram") {
      entry.growth = platformData.followerGrowth ?? 0;
    }

    platforms.push(entry);
  }

  return platforms;
}

export const FollowerPanel: React.FC<FollowerPanelProps> = ({
  report,
  comparison,
  historicalData,
}) => {
  const platforms = extractPlatformFollowers(report, comparison);
  if (platforms.length === 0) {
    return (
      <ReportPanel
        title="Followers"
        icon={icon}
        collapsedBody={<ReportPanelNote>No follower data this month.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="No follower data this month."
          hint="Follower counts appear once a connected platform reports them."
        />
      </ReportPanel>
    );
  }

  const monthsWithFollowers = historicalData.filter((d) => d.followers);
  type DeltaPoint = { month: string } & Record<string, number | string>;
  const deltaData: DeltaPoint[] = [];

  for (let i = 1; i < monthsWithFollowers.length; i++) {
    const curr = monthsWithFollowers[i];
    const prev = monthsWithFollowers[i - 1];
    const point: DeltaPoint = { month: MONTH_NAMES[curr.month - 1] };
    for (const p of platforms) {
      const currCount = curr.followers?.[p.key as keyof typeof curr.followers] ?? 0;
      const prevCount = prev.followers?.[p.key as keyof typeof prev.followers] ?? 0;
      point[p.label] = currCount - prevCount;
    }
    deltaData.push(point);
  }

  const showTrend = deltaData.length >= 2;

  // Folded: every platform's followers together, their change summed only
  // when each platform has one, and the total by month.
  const total = platforms.reduce((sum, p) => sum + p.count, 0);
  const totalChange = platforms.every((p) => p.change)
    ? platforms.reduce((sum, p) => sum + (p.change?.value ?? 0), 0)
    : null;
  const totalByMonth = historicalData.map((d) =>
    d.followers
      ? platforms.reduce(
          (sum, p) => sum + (d.followers?.[p.key as keyof typeof d.followers] ?? 0),
          0,
        )
      : null,
  );
  const extras = platforms.flatMap((p): QuietRow[] => {
    if (p.key === "facebook" && ((p.gained ?? 0) > 0 || (p.lost ?? 0) > 0)) {
      return [
        {
          label: "Facebook gained / lost",
          value: `+${(p.gained ?? 0).toLocaleString()} / -${(p.lost ?? 0).toLocaleString()}`,
        },
      ];
    }
    if (p.key === "instagram" && p.growth !== undefined && p.growth !== 0) {
      return [{ label: "Instagram net growth", value: formatChange(p.growth) }];
    }
    return [];
  });

  return (
    <ReportPanel
      title="Followers"
      icon={icon}
      headlineLabel="Followers, all platforms"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Followers, all platforms"
              value={total.toLocaleString()}
              change={
                totalChange != null
                  ? {
                      value: totalChange,
                      percentage:
                        total - totalChange > 0 ? (totalChange / (total - totalChange)) * 100 : null,
                    }
                  : undefined
              }
              formatChange={formatNumber}
            />
          }
          chart={
            <Sparkline
              values={totalByMonth}
              color={SOURCE_COLOR.social}
              title={`Followers by month, last ${historicalData.length} months`}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {showTrend && (
          <div>
            <VizHeading
              aside={
                <span className="flex items-center gap-3">
                  {platforms.map((p) => (
                    <span key={p.key} className="flex items-center gap-1.5">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: p.color }}
                      />
                      {p.label}
                    </span>
                  ))}
                </span>
              }
            >
              Net change by month
            </VizHeading>
            <ChartBox height={168}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deltaData} barGap={2}>
                  <XAxis dataKey="month" tick={TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={TICK} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatChange(v)} width={45} />
                  <ReferenceLine y={0} stroke="#E5E7EB" />
                  <Tooltip
                    cursor={{ fill: "#F9FAFB" }}
                    formatter={(value: number, name: string) => [formatChange(value), name]}
                  />
                  {/* Each platform keeps its colour below zero too: the bar's
                      side carries the sign, the colour the platform. */}
                  {platforms.map((p) => (
                    <Bar
                      key={p.key}
                      dataKey={p.label}
                      fill={p.color}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={24}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartBox>
          </div>
        )}
        <ReportStats>
          {platforms.map((p) => (
            <ReportStat
              key={p.key}
              label={p.label}
              value={p.count.toLocaleString()}
              change={p.change}
              formatChange={formatNumber}
            />
          ))}
        </ReportStats>
        {extras.length > 0 && <QuietList rows={extras} />}
      </div>
    </ReportPanel>
  );
};
